import {
	AfterViewInit,
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnInit,
	Output,
	QueryList,
	SimpleChanges,
	ViewChildren,
} from "@angular/core";
import { TabComponent } from "../tab/tab.component";

export interface TabConfig<T> {
	id: string;
	label: string;
	selected: boolean;
	data: T;
}
@Component({
	selector: "app-tab-control",
	templateUrl: "./tab-control.component.html",
	styleUrls: ["./tab-control.component.scss"],
})
export class TabControlComponent implements OnInit, OnChanges, AfterViewInit {
	@Input()
	tabs: Array<TabConfig<unknown>> = [];

	@Output()
	selected = new EventEmitter<unknown>();

	@ViewChildren(TabComponent)
	tabComponents: QueryList<TabComponent>;

	tabSet: Array<TabConfig<unknown>> = [];
	focusedIndex = 0;

	private pendingFocusIndex: number | null = null;

	ngOnInit(): void {
		this.tabSet = JSON.parse(JSON.stringify(this.tabs));
		this.focusedIndex = Math.max(
			0,
			this.tabSet.findIndex(tab => tab.selected),
		);
	}

	ngAfterViewInit(): void {
		this.tabComponents.changes.subscribe(() => {
			if (this.pendingFocusIndex !== null) {
				const tabs = this.tabComponents.toArray();
				if (tabs[this.pendingFocusIndex]) {
					tabs[this.pendingFocusIndex].focus();
				}
				this.pendingFocusIndex = null;
			}
		});
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.tabs !== undefined) {
			this.tabSet = JSON.parse(JSON.stringify(changes.tabs.currentValue));
			this.focusedIndex = Math.max(
				0,
				this.tabSet.findIndex(tab => tab.selected),
			);
		}
	}

	selectTab(selectedTab: TabConfig<unknown>) {
		const selectedIndex = this.tabSet.findIndex(tab => tab.label === selectedTab.label);
		this.tabSet.forEach((tab, i) => {
			tab.selected = i === selectedIndex;
		});
		this.focusedIndex = selectedIndex;

		this.selected.emit(selectedTab.data);

		// Restore focus to the activated tab after change detection
		this.focusTabByIndex(selectedIndex);
	}

	onKeydown(event: KeyboardEvent) {
		const tabs = this.tabComponents?.toArray();
		if (!tabs || tabs.length === 0) {
			return;
		}

		let newFocusIndex = this.focusedIndex;

		if (event.key === "ArrowRight") {
			event.preventDefault();
			newFocusIndex = (this.focusedIndex + 1) % tabs.length;
		} else if (event.key === "ArrowLeft") {
			event.preventDefault();
			newFocusIndex = (this.focusedIndex - 1 + tabs.length) % tabs.length;
		} else if (event.key === "Home") {
			event.preventDefault();
			newFocusIndex = 0;
		} else if (event.key === "End") {
			event.preventDefault();
			newFocusIndex = tabs.length - 1;
		} else {
			return;
		}

		// Move focus only — activation requires Enter or Space on the focused tab
		this.focusedIndex = newFocusIndex;
		this.focusTabByIndex(newFocusIndex);
	}

	trackByLabel(_index: number, tab: TabConfig<unknown>): string {
		return tab.label;
	}

	private focusTabByIndex(index: number) {
		const tabs = this.tabComponents?.toArray();
		if (tabs && tabs[index]) {
			setTimeout(() => tabs[index].focus());
		} else {
			this.pendingFocusIndex = index;
		}
	}
}
